import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import SearchPage from './pages/Search';
import ChatPage from './pages/Chat';
import BookDetailPage from './pages/BookDetail';
import BooksPage from './pages/Books';
import SpeciesDetailPage from './pages/SpeciesDetail';
import SourcePagePage from './pages/SourcePage';
import HistoryPage from './pages/History';
import MobilePage from './pages/Mobile';
import AtlasPage from './pages/Atlas';
import { ToastHost } from './toast';
import { trackEvent } from './api';
import { useClientAuth } from './auth';
import LoginDialog, { type AuthDialogMode } from './LoginDialog';
import UiIcon, { type UiIconName } from './UiIcon';

// 标准 HTTP(S) 入口使用同域路径；直连端口与 SSH 隧道仍使用相邻端口。
function siblingAppUrl(configured: string | undefined, devUrl: string, portOffset: number, path: string): string {
  if (configured?.trim()) return configured.trim();
  if (import.meta.env.DEV) return devUrl;
  const url = new URL(window.location.origin);
  const currentPort = Number(url.port);
  if (currentPort > 0) {
    url.port = String(currentPort + portOffset);
    return url.origin;
  }
  return new URL(path, url).href;
}

const ADMIN_URL = siblingAppUrl(import.meta.env.VITE_ADMIN_URL, 'http://localhost:5273', 1, '/admin/');
const PORTAL_URL = siblingAppUrl(import.meta.env.VITE_PORTAL_URL, 'http://localhost:5275', 2, '/portal/');
const TABS: { to: string; label: string; match: (p: string) => boolean; external?: string; adminOnly?: boolean }[] = [
  { to: '/search',   label: 'AI 搜索', match: (p) => p === '/' || p.startsWith('/search') },
  { to: '/chat',     label: '智能问答', match: (p) => p.startsWith('/chat') },
  { to: '/books', label: '图书资料', match: (p) => p.startsWith('/book') || p.startsWith('/species') || p.startsWith('/source-page') },
  { to: '/atlas',    label: '电子图鉴', match: (p) => p.startsWith('/atlas') },
  { to: '/history',  label: '历史会话', match: (p) => p.startsWith('/history') },
  { to: '/admin',    label: '内容管理', match: () => false, external: ADMIN_URL, adminOnly: true },
  { to: '/openapi',  label: '开放平台', match: () => false, external: PORTAL_URL, adminOnly: true },
];

const MOBILE_TABS = [
  { to: '/search', label: '搜索', icon: 'search', match: (p: string) => p === '/' || p.startsWith('/search') },
  { to: '/chat', label: '问答', icon: 'chat', match: (p: string) => p.startsWith('/chat') || p.startsWith('/mobile') },
  { to: '/atlas', label: '图鉴', icon: 'book', match: (p: string) => p.startsWith('/atlas') || p.startsWith('/book') || p.startsWith('/species') || p.startsWith('/source-page') },
  { to: '/history', label: '会话', icon: 'history', match: (p: string) => p.startsWith('/history') },
];

interface AuthGateCopy {
  eyebrow: string;
  title: string;
  description: string;
  loginLabel: string;
  benefits?: [string, string, string];
}

const AUTH_GATE_COPY: Record<'chat' | 'history' | 'sourcePage', AuthGateCopy> = {
  chat: {
    eyebrow: '登录后可保存提问进度',
    title: '把问题交给鱼虾小智',
    description: '登录后即可开始提问。回答会标明资料出处，对话也会自动保存，方便你下次接着聊。',
    loginLabel: '登录并开始提问',
  },
  history: {
    eyebrow: '你的对话记录',
    title: '接着上次的问题继续聊',
    description: '登录后可以查看自己保存的提问、回答和资料出处，并从任何一条对话继续。',
    loginLabel: '登录查看历史对话',
  },
  sourcePage: {
    eyebrow: '登录后核对原始资料',
    title: '查看引用的 PDF 原文页',
    description: '登录后可以核对当前引用的单个物理页，不提供整本 PDF 下载。',
    loginLabel: '登录查看原文页',
    benefits: ['只查看当前引用页', '页码与原 PDF 一致', '不提供整本文件'],
  },
};

function AuthGate({
  children,
  onOpen,
  copy,
  allowPublic = false,
}: {
  children: ReactNode;
  onOpen: (mode: AuthDialogMode) => void;
  copy: AuthGateCopy;
  allowPublic?: boolean;
}) {
  const token = useClientAuth((state) => state.token);
  if (token || allowPublic) return children;
  return (
    <main className="container auth-required-wrap">
      <section className="auth-required glass" aria-labelledby="auth-required-title">
        <div className="auth-required-copy">
          <p className="eyebrow"><span aria-hidden="true"></span>{copy.eyebrow}</p>
          <h1 id="auth-required-title">{copy.title}</h1>
          <p className="auth-required-lead">{copy.description}</p>
          <ul className="auth-required-benefits" aria-label="登录后可用功能">
            {(copy.benefits ?? ['回答附原书出处', '对话自动保存', '电脑和手机都能继续']).map((benefit) => (
              <li key={benefit}><span aria-hidden="true">✓</span>{benefit}</li>
            ))}
          </ul>
          <div className="auth-required-actions">
            <button className="btn primary" type="button" onClick={() => onOpen('login')}>{copy.loginLabel}<span aria-hidden="true">→</span></button>
            <button className="btn" type="button" onClick={() => onOpen('register')}>创建账号</button>
          </div>
          <small>还没有账号？注册只需设置用户名和密码。</small>
        </div>
        <div className="auth-required-preview" aria-hidden="true">
          <div className="auth-preview-glow"></div>
          <div className="auth-preview-window">
            <div className="auth-preview-head">
              <span className="auth-preview-mark">🐟</span>
              <span><b>鱼虾小智</b><small>资料问答助手</small></span>
              <i></i>
            </div>
            <div className="auth-preview-question">青鱼主要分布在哪里？</div>
            <div className="auth-preview-answer">
              <span className="auth-preview-label">已查阅收录资料</span>
              <b>青鱼主要分布在我国华中、华东、西南等地区。</b>
              <span className="auth-preview-source">《国家水产养殖种质资源种类名录》 · P.25</span>
            </div>
            <div className="auth-preview-compose"><span>输入你想了解的问题…</span><b>↑</b></div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const loc = useLocation();
  const [authDialog, setAuthDialog] = useState<AuthDialogMode | null>(null);
  const token = useClientAuth((state) => state.token);
  const user = useClientAuth((state) => state.user);
  const logout = useClientAuth((state) => state.clear);
  // A new page must not inherit the previous detail page's deep scroll offset.
  // Query-only chat changes keep their own conversation scroll behavior.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [loc.pathname]);
  useEffect(() => {
    trackEvent('page_view', { path: loc.pathname });
  }, [loc.pathname]);
  const publicShare = loc.pathname === '/chat' && !!new URLSearchParams(loc.search).get('share')?.trim();
  const isAdmin = user?.roles?.some((role) => ['admin', 'super_admin', 'content_admin'].includes(role)) ?? false;
  const protectedPage = (page: ReactNode, copy: AuthGateCopy) => <AuthGate onOpen={setAuthDialog} copy={copy}>{page}</AuthGate>;
  return (
    <>
      <nav className="nav">
        <span className="logo">
          <span className="logo-mark">🐟</span>
          <span className="brand-full">科普趣答·鱼虾图鉴</span>
          <span className="brand-short">鱼虾图鉴</span>
        </span>
        {TABS.filter((tab) => !tab.adminOnly || isAdmin).map((t) => {
          const active = t.match(loc.pathname);
          const cls = `${active ? 'tab active' : 'tab'}${t.adminOnly ? ' tab-admin' : ''}`;
          if (t.external) {
            return <a key={t.to} className={cls} href={t.external} target="_blank" rel="noreferrer">{t.label}</a>;
          }
          return <Link key={t.to} className={cls} to={t.to}>{t.label}</Link>;
        })}
        <span className="spacer"></span>
        {token && user ? (
          <span className="account-wrap">
            <span className="user" title="当前登录账号">
              <span className="avatar">{(user.nickname?.trim()?.[0] ?? '用').toUpperCase()}</span>
              <span className="account-name">{user.nickname?.trim() || '已登录'}</span>
            </span>
            <button className="account-logout" type="button" onClick={logout}>退出</button>
          </span>
        ) : (
          <button className="user user-action" type="button" onClick={() => setAuthDialog('login')}>
            <span className="avatar">入</span>
            <span className="account-label-full">登录 / 注册</span>
            <span className="account-label-short">登录</span>
          </button>
        )}
      </nav>

      <Routes key={token ?? 'anonymous'}>
        <Route path="/"            element={<Navigate to="/search" replace />} />
        <Route path="/search"      element={<SearchPage />} />
        <Route path="/chat"        element={<AuthGate onOpen={setAuthDialog} copy={AUTH_GATE_COPY.chat} allowPublic={publicShare}><ChatPage /></AuthGate>} />
        <Route path="/books"       element={<BooksPage />} />
        <Route path="/history"     element={protectedPage(<HistoryPage />, AUTH_GATE_COPY.history)} />
        <Route path="/book/:id"    element={<BookDetailPage />} />
        <Route path="/species/:id" element={<SpeciesDetailPage />} />
        <Route path="/source-page/:bookId/:page" element={protectedPage(<SourcePagePage />, AUTH_GATE_COPY.sourcePage)} />
        <Route path="/atlas"      element={<AtlasPage />} />
        <Route path="/mobile"      element={protectedPage(<MobilePage />, AUTH_GATE_COPY.chat)} />
        <Route path="*"            element={<Navigate to="/search" replace />} />
      </Routes>
      <nav className="mobile-bottom-nav" aria-label="移动端主导航">
        {MOBILE_TABS.map((tab) => (
          <Link key={tab.to} className={tab.match(loc.pathname) ? 'active' : ''} aria-current={tab.match(loc.pathname) ? 'page' : undefined} to={tab.to}>
            <span className="mobile-nav-icon" aria-hidden="true"><UiIcon name={tab.icon as UiIconName} /></span>
            <span>{tab.label}</span>
          </Link>
        ))}
      </nav>
      <LoginDialog
        open={authDialog !== null}
        initialMode={authDialog ?? 'login'}
        onClose={() => setAuthDialog(null)}
      />
      <ToastHost />
    </>
  );
}
